/**
 * Settings persistence + parser-config validation shared by the Development
 * tab and the Enquiry Queue parse modal.
 *
 * The Development tab is the canonical authoring surface for prompts, fields,
 * and predictions. The queue tab simply reads those same localStorage keys via
 * `loadSettings()` so a Parse run on a queued enquiry uses the latest config
 * without any prop-drilling between tabs.
 */

import {
	DEFAULT_FIELDS,
	DEFAULT_MODEL,
	DEFAULT_PREDICTIONS,
	DEFAULT_SYSTEM_INSTRUCTIONS,
	DEFAULT_TEMPERATURE,
	DEFAULT_USER_INSTRUCTIONS,
	localStorageKey,
} from "../registry"

export const readLocalStorage = (key, fallback) => {
	try {
		const raw = window.localStorage.getItem(key)
		if (raw === null) return fallback
		return JSON.parse(raw)
	} catch {
		return fallback
	}
}

export const writeLocalStorage = (key, value) => {
	try {
		window.localStorage.setItem(key, JSON.stringify(value))
	} catch {
		// Quota exceeded or storage disabled — silent failure is fine.
	}
}

/**
 * Snapshot of the current parser configuration as authored on the Development
 * tab. Returns plain values so the caller can both run a parse and persist
 * the snapshot back to a queue document for later audit.
 */
export const loadSettings = () => ({
	model: readLocalStorage(localStorageKey("model"), DEFAULT_MODEL),
	temperature: Number(
		readLocalStorage(localStorageKey("temperature"), DEFAULT_TEMPERATURE)
	) || 0,
	system_instructions: readLocalStorage(
		localStorageKey("system"),
		DEFAULT_SYSTEM_INSTRUCTIONS
	),
	user_instructions: readLocalStorage(
		localStorageKey("user"),
		DEFAULT_USER_INSTRUCTIONS
	),
	fields: readLocalStorage(localStorageKey("fields"), DEFAULT_FIELDS),
	predictions: readLocalStorage(localStorageKey("predictions"), DEFAULT_PREDICTIONS),
})

/**
 * Validate field/prediction key sets before sending them to OpenAI. Both
 * lists share the same naming rules — duplicate or invalid keys would only
 * surface as confusing schema errors otherwise.
 */
export const validateSettings = ({ fields, predictions }) => {
	if (!fields || fields.length === 0) {
		throw new Error("Add at least one field for the parser to populate")
	}

	const seenFieldKeys = new Set()
	for (const field of fields) {
		if (!field.key) {
			throw new Error("All fields must have a key")
		}
		if (!/^[a-z][a-z0-9_]*$/.test(field.key)) {
			throw new Error(
				`Field key "${field.key}" is invalid — use lowercase letters, digits, and underscores only`
			)
		}
		if (seenFieldKeys.has(field.key)) {
			throw new Error(`Duplicate field key: "${field.key}"`)
		}
		seenFieldKeys.add(field.key)
	}

	const seenPredictionKeys = new Set()
	for (const prediction of predictions || []) {
		if (!prediction.key) {
			throw new Error("All predictions must have a key")
		}
		if (!/^[a-z][a-z0-9_]*$/.test(prediction.key)) {
			throw new Error(
				`Prediction key "${prediction.key}" is invalid — use lowercase letters, digits, and underscores only`
			)
		}
		if (seenPredictionKeys.has(prediction.key)) {
			throw new Error(`Duplicate prediction key: "${prediction.key}"`)
		}
		seenPredictionKeys.add(prediction.key)
	}
}

export const looksLikeHtml = (input) => /<\/?[a-z][\s\S]*>/i.test(input || "")
